import {ScreenSnapshot} from './pageContextTypes'
import {ScreenListKey, fitScreenSnapshot} from './screenText'

/**
 * READ-ONLY. Everything in this file only reads the page: `querySelectorAll`, `getElementById`,
 * `childNodes`, `textContent`, `getAttribute`, `parentElement`, `offsetParent`,
 * `getComputedStyle`, `document.title`. It never writes an attribute, a class, a value or a
 * node, never focuses, clicks, scrolls or dispatches, and must not reuse the walkthrough's
 * element tagging. The chat only sends what was read; nothing it receives acts on the page.
 */

/** Never read into, wherever they appear: form values, table body cells, hidden or private areas. */
const SKIPPED_TAGS = new Set([
  'INPUT',
  'TEXTAREA',
  'SELECT',
  'OPTION',
  'TD',
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TEMPLATE',
])

/**
 * The applicant's own identity and anything echoing record data on the applicant pages
 * (`/admissions/*`), matched by the classes those pages already render — no markers are added:
 * the dashboard's name/email/avatar block, the greeting that addresses the applicant by name, the
 * status list (applicant id, stage), the existing-parent search result ("Continue with <name>"),
 * and combobox option lists filtered by what the applicant typed.
 */
const APPLICANT_PRIVATE_SELECTOR = [
  '.adm-dash-user',
  '.adm-dash-avatar',
  '.adm-dash-greeting',
  '.adm-dash-status-list',
  '.adm-apply-review-section > .adm-apply-stack-3',
  '.adm-combobox-option',
].join(', ')

/** The applicant pages' content container — they render outside the Metronic layout. */
const APPLICANT_ROOT_SELECTOR = '.admissions-root'
const CHATBOT_WIDGET_SELECTOR = '.chatbot-widget-container'

/** First match wins, so a tab link styled as a button is only a tab. */
const GROUPS: Array<[ScreenListKey, string]> = [
  ['tabs', '[role="tab"], .nav-link, .adm-apply-tab, .adm-apply-stepper-item'],
  ['columns', 'thead th, th[scope="col"]'],
  ['headings', 'h1, h2, h3, h4, .card-title, .card-label'],
  ['labels', 'label, .form-label, .col-form-label, .adm-apply-field-label'],
  ['buttons', 'button, a.btn, .btn'],
]

function isSkipped(element: Element): boolean {
  if (SKIPPED_TAGS.has(element.tagName.toUpperCase())) return true
  if (element.getAttribute('data-private') !== null) return true
  if (element.matches(APPLICANT_PRIVATE_SELECTOR)) return true
  if (element.getAttribute('hidden') !== null) return true
  if (element.getAttribute('aria-hidden') === 'true') return true
  const editable = element.getAttribute('contenteditable')
  if (editable !== null && editable !== 'false') return true
  // The chatbot's own widget is never part of the page it is talking about.
  return (element.getAttribute('class') ?? '').includes('chatbot-widget-container')
}

function isInsideSkipped(element: Element): boolean {
  for (let node: Element | null = element; node; node = node.parentElement) {
    if (isSkipped(node)) return true
  }
  return false
}

function isVisible(element: Element): boolean {
  const view = element.ownerDocument.defaultView
  if (!view) return false
  const style = view.getComputedStyle(element)
  if (style.display === 'none' || style.visibility === 'hidden') return false
  // No offsetParent means not rendered — except for fixed-position elements.
  return (element as HTMLElement).offsetParent !== null || style.position === 'fixed'
}

/** The element's text without descending into anything skipped (a select inside a label). */
function readText(node: Node): string {
  let text = ''
  node.childNodes.forEach((child) => {
    if (child.nodeType === 3) {
      text += child.textContent ?? ''
    } else if (child.nodeType === 1 && !isSkipped(child as Element)) {
      text += ` ${readText(child)}`
    }
  })
  return text
}

function readTitle(doc: Document): string | undefined {
  const heading = doc.getElementById('kt_page_title')?.querySelectorAll('h1')[0]
  return (heading ? readText(heading) : '') || doc.title || undefined
}

/**
 * Where the page's content lives: Metronic's content area, else the applicant pages' own root
 * (never one that is, or holds, the chatbot widget), else the body.
 */
export function resolveSnapshotRoot(doc: Document = document): Element | null {
  const metronic = doc.getElementById('kt_app_content')
  if (metronic) return metronic
  const applicant = [...doc.querySelectorAll(APPLICANT_ROOT_SELECTOR)].find(
    (element) =>
      !element.closest(CHATBOT_WIDGET_SELECTOR) && !element.querySelector(CHATBOT_WIDGET_SELECTOR)
  )
  return applicant ?? doc.body
}

/**
 * The labels of what the user currently sees in the main content area, collected once per
 * message. Values, table cells and anything that looks like data are never included; see
 * `screenText.ts` for the label rules and size budget.
 */
export function collectScreenSnapshot(doc: Document = document): ScreenSnapshot | null {
  const root = resolveSnapshotRoot(doc)
  if (!root) return null

  const claimed = new Set<Element>()
  const lists: Record<ScreenListKey, string[]> = {
    headings: [],
    tabs: [],
    buttons: [],
    labels: [],
    columns: [],
  }

  GROUPS.forEach(([key, selector]) => {
    root.querySelectorAll(selector).forEach((element) => {
      if (claimed.has(element)) return
      claimed.add(element)
      if (isInsideSkipped(element) || !isVisible(element)) return
      lists[key].push(readText(element))
    })
  })

  return fitScreenSnapshot({title: readTitle(doc), ...lists})
}
